import { forwardRef, useCallback, useImperativeHandle, useRef, useState, type VideoHTMLAttributes } from 'react';
import { usePhoneLayout } from './usePhoneLayout';
import { PHONE_INLINE_VIDEO } from './phoneInlineVideo';

interface EditorVideoProps extends VideoHTMLAttributes<HTMLVideoElement> {
  /** Classes for the wrapper the phone version needs. Ignored on desktop. */
  wrapperClassName?: string;
  /**
   * Phone: a tap on the picture plays or pauses, with a play glyph while
   * paused (the default). False for a screen where a tap shows or hides the
   * controls instead and never changes playback (spec 13.16).
   */
  tapToPlay?: boolean;
}

/**
 * A preview <video> inside the editor.
 *
 * Desktop: the element exactly as the caller wrote it, native controls and all.
 *
 * Phone: inline, no native controls, no picture-in-picture, so Safari never
 * swaps in its own player. A tap on the picture plays or pauses, and a play
 * glyph shows while it is paused, the same affordance VideoEditorPlayer gives
 * the main video.
 */
export const EditorVideo = forwardRef<HTMLVideoElement, EditorVideoProps>(
  ({ wrapperClassName, onPlay, onPause, onEnded, tapToPlay = true, ...props }, ref) => {
    const isMobile = usePhoneLayout().phone;
    const videoRef = useRef<HTMLVideoElement>(null);
    useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement);
    /* Starts paused even with autoPlay. iOS refuses an unmuted autoplay without
       firing pause, so trusting autoPlay would hide the glyph on a video that
       is not playing. A successful autoplay fires play and clears it. */
    const [paused, setPaused] = useState(true);

    const toggle = useCallback(() => {
      const v = videoRef.current;
      if (!v) return;
      if (v.paused) v.play().catch(() => {});
      else v.pause();
    }, []);

    if (!isMobile) {
      return <video ref={videoRef} onPlay={onPlay} onPause={onPause} onEnded={onEnded} {...props} />;
    }

    return (
      <div className={`relative ${wrapperClassName ?? ''}`}>
        <video
          ref={videoRef}
          {...props}
          {...PHONE_INLINE_VIDEO}
          controls={false}
          onClick={tapToPlay ? toggle : undefined}
          onPlay={(e) => { setPaused(false); onPlay?.(e); }}
          onPause={(e) => { setPaused(true); onPause?.(e); }}
          onEnded={(e) => { setPaused(true); onEnded?.(e); }}
        />
        {paused && tapToPlay && (
          <button
            type="button"
            onClick={toggle}
            aria-label="Play"
            className="absolute inset-0 flex items-center justify-center"
          >
            <span className="bg-black/50 rounded-full w-12 h-12 flex items-center justify-center pointer-events-none">
              <svg className="w-6 h-6 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </button>
        )}
      </div>
    );
  },
);
EditorVideo.displayName = 'EditorVideo';
